import React, { useState, useRef } from 'react';
import { Container, Typography, Button, TextField, Box, Autocomplete, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { toast } from 'react-toastify';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate } from 'react-router-dom';
import type { FormData } from '../../../types';
import ProductBasicInfo from './ProductBasicInfo';
import ProductDescription from './ProductDescription';
import ProductCategory from './ProductCategory';
import ProductOptions from './ProductOptions';
import WholesaleFields from './WholesaleFields';
import BulkDiscountFields from './BulkDiscountFields';
import ColorVariants from './ColorVariants';
import ProductImages from './ProductImages';
import { addProduct } from '../../../services/api/ProductAPI';

const initialFormData: FormData = {
	name: '',
	category: '',
	description: '',
	price: '',
	quantity: '',
	bestSelling: false,
	trending: false,
	productImage: [],
	weight: '',
	weightUnit: 'kg',
	isInquiry: false,
	isBulkDiscount: false,
	isCustomized: false,
	slug: '',
	isWholesale: false,
	wholesaleFields: [],
	buldDiscountsData: [],
	wholesaleMinQuantity: 1,
	isStickoutPrice: false,
	isColorVariantAvailable: false,
	strickoutPrice: 1,
	colorVariants: [],
	imageAltTags: [],
};

const CreateProductPage = () => {
	const [formData, setFormData] = useState<FormData>(initialFormData);
	const [loading, setLoading] = useState(false);
	const [seo, setSeo] = useState<any>({
		metaTitle: '',
		metaDescription: '',
		canonicalTag: '',
		imageAltTag: '',
		keywords: [],
	});
	const quillRef = useRef<any>(null);
	const navigate = useNavigate();
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.isColorVariantAvailable && formData.productImage.length === 0) {
			toast.error('Please upload at least one image.');
			return;
		}

		const formDataToSend = new FormData();
		formDataToSend.append('name', formData.name);
		formDataToSend.append('slug', formData.slug);
		formDataToSend.append('description', formData.description);
		formDataToSend.append('category', formData.category);
		formDataToSend.append('trending', String(formData.trending));
		formDataToSend.append('metaTitle', seo.metaTitle);
		formDataToSend.append('metaDescription', seo.metaDescription);
		formDataToSend.append('canonicalTag', seo.canonicalTag);
		formData.productImage.forEach((file) => {
			formDataToSend.append('productImage', file);
		});
		seo.keywords.forEach((text: any, index: any) => {
			formDataToSend.append(`keywords[${index}]`, text);
		});

		formData.imageAltTags.forEach((tag: any) => {
			formDataToSend.append('imageAltTag', tag);
		});

		if (!formData.isInquiry) {
			formDataToSend.append('weight', formData.weight);
			if (!formData.isColorVariantAvailable) {
				formDataToSend.append('price', formData.price.toString());
			}
			formDataToSend.append('quantity', formData.quantity);
			formDataToSend.append('bestSelling', String(formData.bestSelling));
			if (formData.isWholesale) {
				formDataToSend.append(
					'wholesaleMinQuantity',
					String(formData.wholesaleMinQuantity)
				);
			}
			formDataToSend.append('weightUnit', formData.weightUnit);
			formDataToSend.append('isBulkDiscount', String(formData.isBulkDiscount));
			formDataToSend.append('isCustomized', String(formData.isCustomized));
			formDataToSend.append('isWholesale', String(formData.isWholesale));
		}

		formDataToSend.append('isInquiry', String(formData.isInquiry));
		formDataToSend.append('isColorVariantAvailable', String(formData.isColorVariantAvailable));
		formDataToSend.append('isStickoutPrice', String(formData.isStickoutPrice));
		if (formData.isStickoutPrice) {
			formDataToSend.append('strickoutPrice', String(formData.strickoutPrice));
		}

		formData.wholesaleFields.forEach((field, index) => {
			formDataToSend.append(`wholesaleFields[${index}][type]`, field.type);
			formDataToSend.append(`wholesaleFields[${index}][label]`, field.label);
			formDataToSend.append(`wholesaleFields[${index}][required]`, String(field.required));
		});

		formData.buldDiscountsData.forEach((discount, index) => {
			formDataToSend.append(
				`bulkDiscounts[${index}][minQuantity]`,
				String(discount.minQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][maxQuantity]`,
				String(discount.maxQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][discountPercentage]`,
				String(discount.discountPercentage)
			);
		});

		formData.colorVariants.forEach((variant, variantIndex) => {
			formDataToSend.append(`colorVariants[${variantIndex}][colorName]`, variant.colorName);
			formDataToSend.append(
				`colorVariants[${variantIndex}][colorCode]`,
				variant.colorCode || '#c62020'
			);
			formDataToSend.append(`colorVariants[${variantIndex}][price]`, String(variant.price));
			formDataToSend.append(
				`colorVariants[${variantIndex}][strickoutPrice]`,
				String(variant.strickoutPrice)
			);

			variant.sizes.forEach((sizeObj, sizeIndex) => {
				formDataToSend.append(
					`colorVariants[${variantIndex}][sizes][${sizeIndex}][size]`,
					sizeObj.size
				);
			});
			(Array.isArray(variant.imageAltTag) ? variant.imageAltTag : [variant.imageAltTag])
				.filter(Boolean)
				.forEach((img: any) => {
					formDataToSend.append(`colorVariants[${variantIndex}][imageAltTag]`, img);
				});
			variant.colorImage.forEach((img, imgIndex) => {
				if (img.file) {
					formDataToSend.append(`colorVariants[${variantIndex}][colorImage]`, img.file);
				} else if (img.url) {
					formDataToSend.append(
						`colorVariants[${variantIndex}][colorImage][${imgIndex}]`,
						img.url
					);
				}
			});
		});

		try {
			setLoading(true);
			const response = await addProduct(formDataToSend);
			if (response.isError) {
				if (response.data.errors) {
					toast.error(response.data.errors);
				} else if (response.data.message) {
					toast.error(response.data.message);
				}
			} else {
				toast.success(response.message || 'Product added successfully!');
				resetForm();
				navigate(location.includes(slug as string) ? `/${slug}/product` : '/product');
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('Failed to save product.');
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData(initialFormData);
		if (quillRef.current) {
			quillRef.current.getEditor().setContents([]);
		}
	};

	return (
		<div>
			<Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
				<Typography
					gutterBottom
					style={{ fontFamily: 'Poppins', fontWeight: '700', fontSize: '24px' }}
				>
					Create New Product
				</Typography>
				<Button
					variant="outlined"
					color="error"
					onClick={() => navigate('/product')}
					startIcon={<KeyboardBackspaceIcon />}
					disabled={loading}
				>
					Go Back
				</Button>
			</Grid>
			<Container>
				<form onSubmit={handleSubmit}>
					<Grid container spacing={2} sx={{ pt: 2 }}>
						<ProductBasicInfo formData={formData} setFormData={setFormData} />

						<ProductDescription
							formData={formData}
							setFormData={setFormData}
							quillRef={quillRef}
						/>

						<ProductCategory formData={formData} setFormData={setFormData} />

						<ProductOptions formData={formData} setFormData={setFormData} />

						{!formData.isInquiry && (
							<>
								{formData.isWholesale && (
									<WholesaleFields
										formData={formData}
										setFormData={setFormData}
									/>
								)}

								{formData.isBulkDiscount && (
									<BulkDiscountFields
										formData={formData}
										setFormData={setFormData}
									/>
								)}
							</>
						)}

						{!formData.isColorVariantAvailable ? (
							<ProductImages formData={formData} setFormData={setFormData} />
						) : (
							<ColorVariants formData={formData} setFormData={setFormData} />
						)}
					</Grid>
					<Box sx={{ pt: 2 }}>
						<strong>SEO</strong> <br />
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="MetaTitle"
										fullWidth
										sx={{ fontFamily: 'Poppins' }}
										placeholder="MetaTitle"
										type="text"
										onChange={(e) =>
											setSeo({ ...seo, metaTitle: e.target.value })
										}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="metaDescription"
										placeholder="metaDescription"
										fullWidth
										sx={{ fontFamily: 'Poppins' }}
										type="text"
										onChange={(e) =>
											setSeo({ ...seo, metaDescription: e.target.value })
										}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="canonicalTag"
										placeholder="canonicalTag"
										type="text"
										sx={{ fontFamily: 'Poppins' }}
										fullWidth
										onChange={(e) =>
											setSeo({ ...seo, canonicalTag: e.target.value })
										}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<Autocomplete
										multiple
										freeSolo
										options={[]}
										value={seo.keywords || []}
										onChange={(_, newValue) =>
											setSeo({
												...seo,
												keywords: newValue
													.map((v) => v.trim())
													.filter((v) => v !== ''),
											})
										}
										renderTags={(value, getTagProps) =>
											value.map((option, index) => (
												<Box key={index}>
													<Chip
														variant="outlined"
														label={option}
														{...getTagProps({ index })}
														sx={{ fontFamily: 'Poppins' }}
													/>
												</Box>
											))
										}
										renderInput={(params) => (
											<TextField
												{...params}
												variant="outlined"
												size="small"
												label="Keywords"
												placeholder="Type and press Enter"
												sx={{ fontFamily: 'Poppins' }}
											/>
										)}
									/>
								</Box>
							</Grid>
						</Grid>
					</Box>
					<Grid container sx={{ mt: 3 }}>
						<Grid size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={2}>
							<Button
								variant="outlined"
								color="error"
								onClick={() => navigate('/product')}
								disabled={loading}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								disabled={loading}
							>
								{loading ? 'Loading...' : 'Create Product'}
							</Button>
						</Grid>
					</Grid>
				</form>
			</Container>
		</div>
	);
};

export default CreateProductPage;
